import React, { useState, useEffect, useCallback } from 'react';
import styles from './EditHistory.module.scss';
import PageTitle from '../../../components/pageTitle/PageTitle';
import Table from '../../../components/table/Table';
import DropDownButton from '../../../components/dropDownButton/DropDownButton';
import ProfilePicture from '../../../components/profilePicture/ProfilePicture';
import Search from '../../../components/search/Search';
import Button from '../../../components/button/Button';
import Icon from '../../../components/icon/Icon';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import { useProgram } from '../../../contexts/ProgramContext';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import { format } from 'date-fns';

const EditHistory = () => {
  const { showToast } = useToasts();
  const { programs, selectedProgramId, setSelectedProgramId } = useProgram();
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    start: 1,
    end: 1,
    hasPrev: false,
    hasNext: false,
  });

  const fetchAuditLogs = useCallback(async (page = 1) => {
    if (!selectedProgramId) return;
    setIsLoading(true);
    try {
      const params = { page, limit: 10 };
      if (search) params.search = search;
      const response = await axiosClient.get(`/program-schedules/${selectedProgramId}/audit-logs`, { params });
      const responseData = response.data;
      setAuditLogs(responseData.data || []);
      setPagination({
        current: responseData.page,
        total: responseData.total,
        start: (responseData.page - 1) * responseData.limit + 1,
        end: Math.min(responseData.page * responseData.limit, responseData.total),
        hasPrev: responseData.page > 1,
        hasNext: responseData.page < responseData.totalPages,
      });
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [selectedProgramId, search, showToast]);

  useEffect(() => { fetchAuditLogs(1); }, [fetchAuditLogs]);

  const handleExport = async () => {
    if (!selectedProgramId) return;
    setIsLoading(true);
    await axiosClient.post('reports/program-schedule-audit-logs', { programId: selectedProgramId }, {
      responseType: 'blob',
      transformResponse: [(data) => data],
    })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement('a');
        link.href = url;
        link.download = 'schedule_audit_logs.xlsx';
        link.click();
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const programOptions = programs.map(p => ({
    label: p.title,
    onClick: () => setSelectedProgramId(p.id),
  }));
  const selectedProgramTitle = programs.find(p => p.id === selectedProgramId)?.title || 'Select Program';

  const columns = [
    {
      header: 'Summary',
      key: 'summary',
      render: (row) => (
        <div className={styles.summaryCell}>
          <span className={styles.action}>{row.action}</span>
          <span className={styles.description}>{row.description}</span>
        </div>
      ),
    },
    {
      header: 'Schedule',
      key: 'schedule',
      render: (row) => format(new Date(row.schedule.date), 'MM/dd/yyyy'),
    },
    {
      header: 'Student',
      key: 'student',
      render: (row) => (
        <div className={styles.userCell}>
          <ProfilePicture
            firstName={row.schedule.student.firstName}
            lastName={row.schedule.student.lastName}
            src={row.schedule.student.profilePicture}
            size="xs"
          />
          <span>{row.schedule.student.firstName} {row.schedule.student.lastName}</span>
        </div>
      ),
    },
    {
      header: 'Changed By',
      key: 'changedBy',
      render: (row) => (
        <div className={styles.userCell}>
          <ProfilePicture
            firstName={row.changedBy.firstName}
            lastName={row.changedBy.lastName}
            src={row.changedBy.profilePicture}
            size="xs"
          />
          <span>{row.changedBy.firstName} {row.changedBy.lastName}</span>
        </div>
      ),
    },
    {
      header: 'Date & Time',
      key: 'createdAt',
      render: (row) => (
        <div className={styles.dateTimeCell}>
          <span>{format(new Date(row.createdAt), 'MM/dd/yyyy')}</span>
          <span className={styles.time}>{format(new Date(row.createdAt), 'hh:mm a')}</span>
        </div>
      ),
    },
  ];

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <PageTitle label="Edit History" />
          <DropDownButton
            label={selectedProgramTitle}
            items={programOptions}
            type="button"
            size="small"
            variant="primary-light"
            position="bottom-left"
          />
        </div>
        <div className={styles.headerActions}>
          <Search onSubmit={setSearch} />
          <Button
            label={<span className={styles.exportLabel}><Icon name="download" width="16" />Export</span>}
            size="small"
            variant="primary-light"
            onClick={handleExport}
          />
        </div>
      </div>
      <div className={styles.container}>
        <Table
          columns={columns}
          data={auditLogs}
          pagination={pagination}
          onPageChange={fetchAuditLogs}
          emptyMessage="No edit history found for this program."
        />
      </div>
    </>
  );
};

export default EditHistory;
